// src/pages/widgets/WidgetTemplateListPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Link as MuiLink,
  Chip,
  Stack,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ViewVersionsIcon,
  Code as EmbedIcon, // Added
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { widgetService } from '../../api/widgetService';
import EmbedCodeModal from '../../components/widgets/EmbedCodeModal'; // Added
import IdDisplayWithCopy from '../../components/common/IdDisplayWithCopy'; // Import the new component

const WidgetTemplateListPage = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // State for EmbedCodeModal
  const [embedModalProps, setEmbedModalProps] = useState({
    open: false,
    versionId: null,
    templateId: null,
    templateName: null,
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    const res = await widgetService.listTemplates();
    if (res.isError) {
      setError(res.message || 'Failed to fetch widget templates.');
      setTemplates([]);
      setFilteredTemplates([]);
      toast.error(res.message || 'Failed to fetch widget templates.');
    } else {
      const validTemplates = Array.isArray(res) ? res : [];
      // Sort templates, e.g., by updated_at descending
      validTemplates.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setTemplates(validTemplates);
      setFilteredTemplates(validTemplates);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
    } else {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const filtered = templates.filter(
        (tpl) =>
          tpl.name.toLowerCase().includes(lowerCaseQuery) ||
          (tpl.description && tpl.description.toLowerCase().includes(lowerCaseQuery))
      );
      setFilteredTemplates(filtered);
    }
  }, [searchQuery, templates]);

  const handleOpenDeleteDialog = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setTemplateToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    setLoading(true); // Indicate loading during delete
    const res = await widgetService.deleteTemplate(templateToDelete.id);
    setLoading(false);
    handleCloseDeleteDialog();

    if (res.isError) {
      toast.error(res.message || 'Failed to delete template.');
    } else {
      toast.success(`Template "${templateToDelete.name}" deleted successfully.`);
      fetchTemplates(); // Refetch to update list
    }
  };

  const handleRefresh = () => {
    setSearchQuery(''); // Optionally clear search on refresh
    fetchTemplates();
  };

  const handleOpenEmbedModalForTemplate = (template) => {
    setEmbedModalProps({
      open: true,
      versionId: null, // Not embedding a specific version from this list context
      templateId: template.id,
      templateName: template.name,
    });
  };

  const handleCloseEmbedModal = () => {
    setEmbedModalProps({ open: false, versionId: null, templateId: null, templateName: null });
  };


  if (loading && templates.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 5, height: 'calc(100vh - 200px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} spacing={2}>
        <Typography variant="h4">Widget Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/widgets/new')}
        >
          Create New Template
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ mb: 2, p: 2 }}>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center">
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search templates by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon />
                    </InputAdornment>
                    ),
                }}
            />
            <Tooltip title="Refresh list">
                <span>
                <IconButton onClick={handleRefresh} disabled={loading}>
                    {loading && templates.length > 0 ? <CircularProgress size={24} /> : <RefreshIcon />} {/* Show loader if refreshing existing list */}
                </IconButton>
                </span>
            </Tooltip>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Active Versions</TableCell> {/* You might need to fetch this count */}
              <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && templates.length === 0 ? ( //Handles case where search is active during initial load
                <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
            ) : filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1">
                    {searchQuery ? "No templates match your search." : "No widget templates found."}
                  </Typography>
                  {!searchQuery && (
                    <Button sx={{mt:1}} variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/widgets/new')}>
                        Create Your First Template
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((tpl) => (
                <TableRow key={tpl.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    <MuiLink component={RouterLink} to={`/widgets/${tpl.id}/versions`} underline="hover" fontWeight="medium">
                      {tpl.name}
                    </MuiLink>
                    <Typography variant="caption" display="block" color="text.secondary">ID: ...{tpl.id.slice(-6)}</Typography>
                    <IdDisplayWithCopy id={tpl.id} label="Template ID" /> {/* USE THE COMPONENT */}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tpl.description || <Typography variant="caption" color="text.secondary">N/A</Typography>}
                  </TableCell>
                  <TableCell>
                    {/* Placeholder for active versions count - requires backend to provide this */}
                    <Chip label={tpl.active_versions_count === undefined ? 'N/A' : tpl.active_versions_count} size="small" />
                  </TableCell>
                  <TableCell>
                    {new Date(tpl.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Manage Versions">
                      <IconButton size="small" onClick={() => navigate(`/widgets/${tpl.id}/versions`)}>
                        <ViewVersionsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Get Embed Code">
                      <IconButton size="small" onClick={() => handleOpenEmbedModalForTemplate(tpl)}>
                        <EmbedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Template Settings">
                      <IconButton size="small" onClick={() => navigate(`/widgets/${tpl.id}/edit`)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Template">
                      <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(tpl)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Widget Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template "<strong>{templateToDelete?.name}</strong>"?
            This will also delete all its associated widget versions. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={loading}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete} autoFocus disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit"/> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmbedCodeModal
        open={embedModalProps.open}
        onClose={handleCloseEmbedModal}
        versionId={embedModalProps.versionId}
        templateId={embedModalProps.templateId}
        templateName={embedModalProps.templateName}
      />
    </Box>
  );
};

export default WidgetTemplateListPage;